// MARK://dhk8/pos/features/TableSelectorBar
// Order-context strip for the 534×738 portrait POS comanda + the "abrir / cambiar
// mesa" sheet. The strip is a two-line deliberate header:
//   line 1 — Mesa <code> · <n> comensales        (prominent)
//   line 2 — <mesero> · <folio>                   (muted)
// with the Cambiar / Cerrar (or Seleccionar mesa) buttons right-aligned.
// The dialog is a BottomSheet: table options as FastBar-style pill buttons, a
// labelled comensales stepper (≥44pt hit targets) and a spaced ghost/green footer.
// All `--pos-*` tokens → theme-aware (light + dark) — no hardcoded hex.
// Port of sc-restaurant TableSelectorBar.tsx.

import { useState } from 'react'
import { UtensilsCrossed, Edit2, X, Minus, Plus } from 'lucide-react'
import type { TableDto, ActiveTableSessionDto } from '@dockethub/ui-restaurant'
import { useOrderStore } from '../stores/order-store.js'
import { useAuthStore } from '../stores/auth-store.js'
import { getFloorState, openTableSession } from '../lib/clients/floor.js'
import { findOpenOrderByTableSession } from '../lib/clients/orders.js'
import { useTenantId } from '../hooks/useTenantId.js'
import BottomSheet from './pos/BottomSheet.js'

export interface TableSelectorBarProps {
  errorMessage?: string | null
  onOpened?: () => void
}

export default function TableSelectorBar({ errorMessage, onOpened }: TableSelectorBarProps) {
  const { user } = useAuthStore()
  const order = useOrderStore((s) => s.order)
  const table = useOrderStore((s) => s.table)
  const setTable = useOrderStore((s) => s.setTable)
  const createOrder = useOrderStore((s) => s.createOrder)
  const resetOrder = useOrderStore((s) => s.resetOrder)
  const hydrateFromBackend = useOrderStore((s) => s.hydrateFromBackend)
  const tenantId = useTenantId()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [covers, setCovers] = useState(table.covers)
  const [tableCode, setTableCode] = useState(table.tableCode ?? '')
  const [picked, setPicked] = useState<TableDto | null>(null)
  const [busy, setBusy] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const [tables, setTables] = useState<TableDto[]>([])
  const [sessions, setSessions] = useState<ActiveTableSessionDto[]>([])
  const [loadingTables, setLoadingTables] = useState(false)

  async function loadTables() {
    setLoadingTables(true)
    try {
      const s = await getFloorState()
      setTables(s.tables)
      setSessions(s.activeSessions ?? [])
    } catch {
      // fallback: manual code entry
    } finally {
      setLoadingTables(false)
    }
  }

  function handleOpenDialog() {
    setCovers(table.covers)
    setTableCode(table.tableCode ?? '')
    setPicked(null)
    setLocalError(null)
    setDialogOpen(true)
    void loadTables()
  }

  async function handleOpen() {
    setBusy(true)
    setLocalError(null)
    try {
      let tableSessionId: string | null = null
      // If a real table was picked, open a floor session (sets it OCCUPIED + links the order).
      if (picked && user) {
        const session = await openTableSession(picked.id, { covers, serverId: user.id }).catch(() => null)
        tableSessionId = session?.id ?? null
      }
      setTable({
        tableCode: picked?.code ?? (tableCode.trim() || null),
        tableSessionId,
        covers,
        waiterName: user?.fullName ?? null,
      })
      await createOrder({ covers, ...(tableSessionId ? { tableSessionId } : {}) })
      setDialogOpen(false)
      onOpened?.()
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'No se pudo abrir la mesa')
    } finally {
      setBusy(false)
    }
  }

  /** Resume an existing order for an occupied table — hydrate the order store with that
   *  order, set the table context, and close the sheet. The table session id comes from
   *  the active session (already OCCUPIED on the floor).
   *
   *  Si la sesión está OCUPADA pero NO tiene orderId (ni cacheado ni recuperable por
   *  tableSessionId), NO bloqueamos al mesero con "sin orden asociada": eso pasa cuando
   *  la sesión se abrió y el cliente quedó huérfano por crash/red/abandonar el flow.
   *  Lo correcto operativamente es CREAR una orden nueva en esa misma sesión y abrirla,
   *  para que el mesero pueda servir/cobrar sin recrear la mesa entera. */
  async function handleResume(t: TableDto, session: ActiveTableSessionDto) {
    setBusy(true)
    setLocalError(null)
    try {
      // PosTableSession.orderId may come back null for demo / legacy sessions even
      // though an OPEN order is tied to the session — recover it by querying
      // /v1/orders?tableSessionId=… before giving up.
      let orderId = session.orderId
      if (!orderId && tenantId) {
        const recovered = await findOpenOrderByTableSession(tenantId, session.id).catch(() => null)
        orderId = recovered?.id ?? null
      }
      if (orderId) {
        // Caso normal: orden existente — hidratar y abrir.
        const fresh = await hydrateFromBackend(orderId)
        if (!fresh) {
          setLocalError('No se pudo retomar la orden de la mesa')
          return
        }
      } else {
        // Caso huérfano: sesión OCCUPIED sin orden recuperable. Crear orden nueva
        // linkeada a esta sesión existente — NO abrir una nueva sesión (la mesa
        // ya está ocupada en el plano, no doblar). createOrder usa el covers de
        // la sesión actual para que el folio refleje los comensales reales.
        setTable({
          tableCode: t.code,
          tableSessionId: session.id,
          covers: session.covers,
          waiterName: user?.fullName ?? null,
        })
        await createOrder({ covers: session.covers, tableSessionId: session.id })
        setDialogOpen(false)
        onOpened?.()
        return
      }
      setTable({
        tableCode: t.code,
        tableSessionId: session.id,
        covers: session.covers,
        waiterName: user?.fullName ?? null,
      })
      setDialogOpen(false)
      onOpened?.()
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'No se pudo retomar la mesa')
    } finally {
      setBusy(false)
    }
  }

  function handleClear() {
    resetOrder()
    setTableCode('')
    setCovers(1)
  }

  // ── group tables by status ─────────────────────────────────────────────
  // Lane #167 fix: source-of-truth es PosTable.status, NO la presencia de
  // una session activa. Antes hacíamos `t.status === OCCUPIED OR sessionByTableId.has(t.id)`,
  // pero eso pintaba como OCUPADAS a mesas que tenían sesiones zombie
  // (closedAt=null) aunque la mesa ya estuviera AVAILABLE en /floor —
  // generaba inconsistencia entre las dos vistas. Ahora confiamos en
  // PosTable.status (que es lo que /floor también pinta).
  // La session metadata sigue disponible para mostrar folio/elapsed/covers.
  const sessionByTableId = new Map<string, ActiveTableSessionDto>()
  for (const s of sessions) sessionByTableId.set(s.tableId, s)
  const occupied = tables.filter((t) => t.status === 'OCCUPIED')
  const free = tables.filter((t) => t.status !== 'OCCUPIED' && t.status !== 'UNAVAILABLE')
  const tableLabel = table.tableCode ?? (order ? 'Sin mesa' : '—')
  const waiterLabel = table.waiterName ?? user?.fullName ?? '—'

  function elapsedLabel(openedAt: string): string {
    const ms = Date.now() - new Date(openedAt).getTime()
    if (Number.isNaN(ms) || ms < 0) return ''
    const m = Math.floor(ms / 60000)
    if (m < 1) return 'ahora'
    if (m < 60) return `${m} min`
    const h = Math.floor(m / 60)
    const rem = m % 60
    return rem === 0 ? `${h} h` : `${h} h ${rem} min`
  }

  return (
    <>
      <div className="dhk8-octx">
        <UtensilsCrossed size={16} className="dhk8-octx__icon" aria-hidden />
        <div className="dhk8-octx__lines">
          <div className="dhk8-octx__l1">
            <span className="dhk8-octx__mesa">Mesa {tableLabel}</span>
            {order && <span className="dhk8-octx__dot">·</span>}
            {order && <span className="dhk8-octx__covers">{table.covers} comensal{table.covers === 1 ? '' : 'es'}</span>}
          </div>
          <div className="dhk8-octx__l2">
            <span>{waiterLabel}</span>
            {order && <><span className="dhk8-octx__dot">·</span><span className="dhk8-octx__folio">{order.folio}</span></>}
          </div>
        </div>
        <div className="dhk8-octx__actions">
          {errorMessage && <span className="dhk8-octx__error">{errorMessage}</span>}
          {!order ? (
            <button type="button" className="dhk8-octx__btn dhk8-octx__btn--primary" onClick={handleOpenDialog}>
              Seleccionar mesa
            </button>
          ) : (
            <>
              <button type="button" className="dhk8-octx__btn" onClick={handleOpenDialog}>
                <Edit2 size={13} /> Cambiar
              </button>
              <button type="button" className="dhk8-octx__btn" onClick={handleClear}>
                <X size={13} /> Cerrar
              </button>
            </>
          )}
        </div>
      </div>

      <BottomSheet
        open={dialogOpen}
        onClose={() => !busy && setDialogOpen(false)}
        title="Mesas del salón"
        heightFraction={0.82}
        footer={
          <div className="dhk8-tsd__foot">
            <button type="button" className="dhk8-tsd__btn dhk8-tsd__btn--ghost" onClick={() => setDialogOpen(false)} disabled={busy}>
              Cancelar
            </button>
            <button
              type="button"
              className="dhk8-tsd__btn dhk8-tsd__btn--primary"
              onClick={() => void handleOpen()}
              disabled={busy || (!picked && !tableCode.trim())}
              title={picked ? `Abrir ${picked.code}` : tableCode.trim() ? `Abrir ${tableCode.trim()}` : 'Selecciona una mesa libre o escribe un código'}
            >
              {busy ? 'Abriendo…' : picked ? `Abrir ${picked.code}` : 'Abrir mesa'}
            </button>
          </div>
        }
      >
        <div className="dhk8-tsd">
          {loadingTables ? (
            <div className="dhk8-tsd__loading">Cargando mesas…</div>
          ) : (
            <>
              {occupied.length > 0 && (
                <div className="dhk8-tsd__field">
                  <span className="dhk8-tsd__label">Ocupadas · toca para retomar</span>
                  <div className="dhk8-tsd__rows">
                    {occupied.map((t) => {
                      const s = sessionByTableId.get(t.id) ?? null
                      const elapsed = s ? elapsedLabel(s.openedAt) : ''
                      return (
                        <button
                          key={t.id}
                          type="button"
                          className="dhk8-tsd__row dhk8-tsd__row--occupied"
                          onClick={() => s && void handleResume(t, s)}
                          disabled={busy || !s}
                        >
                          <span className="dhk8-tsd__row-code">{t.code}</span>
                          <span className="dhk8-tsd__row-meta">
                            {s ? (
                              <>
                                <span>{s.covers} comensal{s.covers === 1 ? '' : 'es'}</span>
                                {elapsed && <><span className="dhk8-tsd__row-dot">·</span><span>{elapsed}</span></>}
                                {!s.orderId && <><span className="dhk8-tsd__row-dot">·</span><span className="dhk8-tsd__row-warn">sin folio</span></>}
                              </>
                            ) : (
                              <span className="dhk8-tsd__row-warn">Ocupada (sin sesión)</span>
                            )}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {free.length > 0 && (
                <div className="dhk8-tsd__field">
                  <span className="dhk8-tsd__label">Libres</span>
                  <div className="dhk8-tsd__tables">
                    {free.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className={`dhk8-tsd__chip${picked?.id === t.id ? ' dhk8-tsd__chip--on' : ''}`}
                        onClick={() => { setPicked(t); setTableCode(t.code) }}
                      >
                        {t.code}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {occupied.length === 0 && free.length === 0 && (
                <div className="dhk8-tsd__field">
                  <span className="dhk8-tsd__label">Captura manual</span>
                  <input
                    type="text"
                    className="dhk8-tsd__input"
                    value={tableCode}
                    onChange={(e) => { setTableCode(e.target.value); setPicked(null) }}
                    placeholder="Código de mesa — ej. M-01"
                    inputMode="text"
                  />
                </div>
              )}
            </>
          )}

          <div className="dhk8-tsd__field">
            <span className="dhk8-tsd__label">Comensales (mesa nueva)</span>
            <div className="dhk8-tsd__stepper">
              <button
                type="button"
                className="dhk8-tsd__step"
                aria-label="Reducir comensales"
                disabled={covers <= 1}
                onClick={() => setCovers((c) => Math.max(1, c - 1))}
              >
                <Minus size={18} />
              </button>
              <span className="dhk8-tsd__step-val" aria-live="polite">{covers}</span>
              <button
                type="button"
                className="dhk8-tsd__step"
                aria-label="Aumentar comensales"
                disabled={covers >= 50}
                onClick={() => setCovers((c) => Math.min(50, c + 1))}
              >
                <Plus size={18} />
              </button>
            </div>
          </div>

          {localError && <div className="dhk8-tsd__alert">{localError}</div>}
        </div>
      </BottomSheet>

      <style>{styles}</style>
    </>
  )
}

const styles = `
  /* ── order-context strip ── */
  .dhk8-octx { display:flex; align-items:center; gap:var(--pos-space-3,12px); padding:var(--pos-space-2,8px) var(--pos-space-3,12px); min-height:var(--pos-touch-target,44px); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-lg,12px); background:var(--pos-surface,#161C18); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }
  .dhk8-octx__icon { flex-shrink:0; color:var(--pos-tint,#1F8B3A); }
  .dhk8-octx__lines { flex:1; min-width:0; display:flex; flex-direction:column; gap:1px; }
  .dhk8-octx__l1 { display:flex; align-items:baseline; gap:6px; min-width:0; }
  .dhk8-octx__mesa { font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .dhk8-octx__covers { font-size:var(--pos-text-sm,13px); color:var(--pos-fg-secondary,#B6BFB8); white-space:nowrap; }
  .dhk8-octx__l2 { display:flex; align-items:baseline; gap:6px; font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); min-width:0; }
  .dhk8-octx__l2 > span:first-child { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .dhk8-octx__folio { font-family:var(--font-mono,ui-monospace); white-space:nowrap; }
  .dhk8-octx__dot { color:var(--pos-fg-tertiary,#7E8C82); flex-shrink:0; }
  .dhk8-octx__actions { display:flex; align-items:center; gap:var(--pos-space-2,8px); flex-shrink:0; }
  .dhk8-octx__error { font-size:var(--pos-text-xxs,11px); color:var(--pos-danger,#E5484D); max-width:120px; line-height:1.2; }
  .dhk8-octx__btn { display:inline-flex; align-items:center; gap:4px; padding:0 var(--pos-space-3,12px); min-height:36px; border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-sm,6px); background:transparent; color:var(--pos-fg-secondary,#B6BFB8); font:inherit; font-size:var(--pos-text-xs,12px); font-weight:var(--pos-weight-medium,500); cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease; }
  .dhk8-octx__btn:hover { background:var(--pos-elevated,#1E2520); color:var(--pos-fg,#E8EFE9); }
  .dhk8-octx__btn--primary { min-height:36px; background:var(--pos-tint,#1F8B3A); border-color:var(--pos-tint,#1F8B3A); color:#fff; font-size:var(--pos-text-sm,13px); padding:0 var(--pos-space-4,16px); }
  .dhk8-octx__btn--primary:hover { background:var(--pos-tint,#1F8B3A); filter:brightness(1.06); }

  /* ── abrir / cambiar mesa sheet ── */
  .dhk8-tsd { display:flex; flex-direction:column; gap:var(--pos-space-5,20px); padding:var(--pos-space-3,12px) var(--pos-space-4,16px) var(--pos-space-4,16px); }
  .dhk8-tsd__field { display:flex; flex-direction:column; gap:var(--pos-space-3,12px); }
  .dhk8-tsd__label { font-size:var(--pos-text-xxs,11px); letter-spacing:.08em; text-transform:uppercase; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-tsd__loading { font-size:var(--pos-text-sm,13px); color:var(--pos-fg-tertiary,#7E8C82); padding:var(--pos-space-3,12px) 0; }
  .dhk8-tsd__tables { display:flex; flex-wrap:wrap; gap:var(--pos-space-2,8px); }
  .dhk8-tsd__chip { min-width:56px; min-height:var(--pos-touch-target,44px); padding:0 var(--pos-space-3,12px); display:inline-flex; align-items:center; justify-content:center; border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-pill,999px); background:transparent; color:var(--pos-fg,#E8EFE9); font:inherit; font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500); font-variant-numeric:tabular-nums; cursor:pointer; transition:all var(--pos-duration-fast,120ms) ease; }
  .dhk8-tsd__chip:hover { background:var(--pos-elevated,#1E2520); }
  .dhk8-tsd__chip--on { background:var(--pos-tint,#1F8B3A); border-color:var(--pos-tint,#1F8B3A); color:#fff; box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }
  .dhk8-tsd__rows { display:flex; flex-direction:column; gap:var(--pos-space-2,8px); }
  .dhk8-tsd__row { display:flex; align-items:center; justify-content:space-between; gap:var(--pos-space-3,12px); min-height:var(--pos-touch-target,44px); padding:var(--pos-space-2,8px) var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-canvas,#0D110F); color:var(--pos-fg,#E8EFE9); font:inherit; font-size:var(--pos-text-sm,13px); text-align:left; cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease, border-color var(--pos-duration-fast,120ms) ease; }
  .dhk8-tsd__row:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); border-color:var(--pos-tint,#1F8B3A); }
  .dhk8-tsd__row:disabled { opacity:.55; cursor:not-allowed; }
  .dhk8-tsd__row-code { font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-semibold,600); font-variant-numeric:tabular-nums; }
  .dhk8-tsd__row-meta { display:inline-flex; align-items:center; gap:6px; font-size:var(--pos-text-xs,12px); color:var(--pos-fg-secondary,#B6BFB8); }
  .dhk8-tsd__row-dot { color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-tsd__row-warn { color:var(--pos-danger,#E5484D); }
  .dhk8-tsd__input { width:100%; min-height:var(--pos-touch-target,44px); padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-canvas,#0D110F); color:var(--pos-fg,#E8EFE9); font:inherit; font-size:var(--pos-text-md,15px); outline:none; transition:border-color var(--pos-duration-fast,120ms) ease, box-shadow var(--pos-duration-fast,120ms) ease; }
  .dhk8-tsd__input:focus { border-color:var(--pos-tint,#1F8B3A); box-shadow:0 0 0 3px var(--pos-tint-soft,rgba(31,139,58,.12)); }
  .dhk8-tsd__stepper { display:flex; align-items:center; gap:var(--pos-space-4,16px); align-self:flex-start; }
  .dhk8-tsd__step { width:44px; height:44px; display:flex; align-items:center; justify-content:center; border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-surface,#161C18); color:var(--pos-fg,#E8EFE9); cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease; }
  .dhk8-tsd__step:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); }
  .dhk8-tsd__step:disabled { opacity:.4; cursor:not-allowed; }
  .dhk8-tsd__step-val { min-width:44px; text-align:center; font-size:var(--pos-text-2xl,22px); font-weight:var(--pos-weight-semibold,600); font-variant-numeric:tabular-nums; color:var(--pos-fg,#E8EFE9); }
  .dhk8-tsd__alert { color:var(--pos-danger,#E5484D); background:color-mix(in oklch, var(--pos-danger,#E5484D) 14%, transparent); border:1px solid color-mix(in oklch, var(--pos-danger,#E5484D) 40%, transparent); border-radius:var(--pos-radius-md,8px); padding:var(--pos-space-3,12px); font-size:var(--pos-text-sm,13px); }
  .dhk8-tsd__foot { display:flex; gap:var(--pos-space-3,12px); padding:var(--pos-space-3,12px) var(--pos-space-4,16px) var(--pos-space-4,16px); }
  .dhk8-tsd__btn { flex:1; min-height:var(--pos-touch-target,44px); display:inline-flex; align-items:center; justify-content:center; border-radius:var(--pos-radius-md,8px); font:inherit; font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-medium,500); cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease; }
  .dhk8-tsd__btn:disabled { opacity:.5; cursor:not-allowed; }
  .dhk8-tsd__btn--ghost { border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); background:transparent; color:var(--pos-fg,#E8EFE9); }
  .dhk8-tsd__btn--ghost:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); }
  .dhk8-tsd__btn--primary { border:1px solid var(--pos-tint,#1F8B3A); background:var(--pos-tint,#1F8B3A); color:#fff; box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); font-weight:var(--pos-weight-semibold,600); }
  .dhk8-tsd__btn--primary:hover:not(:disabled) { filter:brightness(1.06); }
`
